const es = {
  header: {
    badge: "Guía Oficial de BotMix",
    title: "Gestión Integral de Contactos y Directorio CRM",
    summary:
      "Directorio centralizado de clientes, prospectos y grupos. Permite administrar nombres, números internacionales de WhatsApp, correos, campos personalizados dinámicos, asignación a billeteras comerciales y sincronización masiva mediante importación y exportación.",
  },
  sections: [
    {
      id: "datos_basicos",
      title: "1. Datos Básicos y Formato Telefónico",
      description:
        "Registro seguro de la identidad y conectividad del contacto.",
      items: [
        {
          id: "telefono_internacional",
          title: "Número Internacional WhatsApp",
          definition:
            "Debe ingresarse con el código de país sin símbolos especiales (ej: 51987654321, 5511999998888). BotMix valida el número para asegurar la entrega de mensajes.",
        },
        {
          id: "nombre_identificador",
          title: "Nombre y Alias Comercial",
          definition:
            "Identificación visible en chats, bandejas de entrada y tickets. Puedes actualizarlo manualmente o sincronizarlo con el perfil público de WhatsApp del cliente.",
        },
        {
          id: "correo_electronico",
          title: "Correo Electrónico y Notas",
          definition:
            "Información complementaria para comunicaciones multicanal y seguimiento histórico de interacciones.",
        },
      ],
    },
    {
      id: "campos_personalizados",
      title: "2. Campos Personalizados Dinámicos",
      description:
        "Atributos a medida para enriquecer la ficha del cliente.",
      items: [
        {
          id: "atributos_extra",
          title: "Atributos Adicionales (Clave-Valor)",
          definition:
            "Agrega campos como 'Ciudad', 'DNI/RUC', 'Fecha de Cumpleaños', 'Tipo de Plan' o 'Dirección'. Estos campos se pueden utilizar como variables en respuestas rápidas y plantillas.",
        },
        {
          id: "variables_mensajes",
          title: "Uso en Respuestas y Automatizaciones",
          definition:
            "Los valores personalizados pueden insertarse en mensajes para lograr una comunicación 100% personalizada y cercana con cada cliente.",
        },
      ],
    },
    {
      id: "billetera_responsable",
      title: "3. Asignación de Billetera y Usuario Responsable",
      description:
        "Control comercial y direccionamiento automático de conversaciones.",
      items: [
        {
          id: "billetera_comercial",
          title: "Billetera / Asesor Asignado",
          definition:
            "Vincula el contacto a un agente o equipo comercial determinado. Cuando el contacto escriba, el sistema puede priorizar transferir su ticket directamente a su responsable.",
        },
        {
          id: "control_acceso",
          title: "Segmentación y Privacidad",
          definition:
            "Permite limitar la visibilidad de los contactos según el perfil y permisos configurados para los asesores comerciales.",
        },
      ],
    },
    {
      id: "import_export_sync",
      title: "4. Importación, Exportación y Grupos de WhatsApp",
      description:
        "Herramientas masivas para migrar y respaldar tu base de datos.",
      items: [
        {
          id: "importacion_masiva",
          title: "Importación desde Archivos (CSV / Excel)",
          definition:
            "Carga masiva de listas de contactos existentes con un solo clic. El asistente procesa y valida los números telefónicos automáticamente.",
        },
        {
          id: "sincronizar_grupos",
          title: "Sincronización de Grupos de WhatsApp",
          definition:
            "Importa automáticamente todos los participantes de tus grupos de WhatsApp hacia la base de contactos de BotMix para campañas de difusión.",
        },
        {
          id: "exportacion_datos",
          title: "Exportación de Contactos",
          definition:
            "Descarga tu base filtrada en formato compatible con hojas de cálculo para auditoría o campañas externas.",
        },
        {
          id: "extraer_contactos_grupo",
          title: "Extracción de Contactos de Grupos (Botón Azul)",
          definition:
            "Descarga una lista en CSV de los participantes del grupo. Nota de seguridad: En grupos con privacidad activa, WhatsApp anonimiza a los integrantes como identificadores LID (códigos de 15 dígitos) en lugar de números de teléfono para proteger su privacidad.",
        },
        {
          id: "campana_directa_grupo",
          title: "Campaña Directa a Miembros del Grupo (Botón Rojo)",
          definition:
            "Permite enviar mensajes de invitación uno a uno a los participantes del grupo sin salir de la plataforma. Cuenta con validación de membresía en tiempo real (solo la línea que pertenece al grupo puede enviar), goteo forzado Tier 1 (máx. 30 contactos/día) y ventana horaria hábil de 09:00 a 18:00 para evitar bloqueos por Meta.",
        },
        {
          id: "regla_seguridad_lids",
          title: "Incompatibilidad de LIDs en Listas de Campañas Externas",
          definition:
            "⚠️ REGLA DE ORO: Las listas exportadas de grupos contienen LIDs y NO deben importarse en 'Listas de Contactos' de campañas regulares. Meta detecta el contacto no autorizado a LIDs desde números externos como scraping y sanciona con baneo instantáneo. El sistema bloquea automáticamente la carga de estos archivos para proteger tus números.",
        },
      ],
    },
    {
      id: "etiquetado_filtros",
      title: "5. Etiquetas, Marcas y Filtros Inteligentes",
      description:
        "Organización ágil para campañas y seguimiento de clientes.",
      items: [
        {
          id: "etiquetas_contacto",
          title: "Etiquetas de Segmentación",
          definition:
            "Asigna etiquetas de colores para identificar estados (ej: 'Cliente VIP', 'Moroso', 'Prospecto Caliente').",
        },
        {
          id: "filtro_marcas",
          title: "Filtro por Marcas y Búsqueda Rápida",
          definition:
            "Segmenta tu cartera por marca comercial o busca al instante por nombre, número o empresa asociada.",
        },
      ],
    },
  ],
};

const en = {
  header: {
    badge: "BotMix Official Guide",
    title: "Comprehensive Contact Management & CRM Directory",
    summary:
      "Centralized directory of customers, leads, and groups. Manage names, international WhatsApp numbers, emails, dynamic custom fields, sales wallet assignments, and mass synchronization through import and export tools.",
  },
  sections: [
    {
      id: "datos_basicos",
      title: "1. Basic Data & Phone Formatting",
      description:
        "Secure registration of contact identity and communication channels.",
      items: [
        {
          id: "telefono_internacional",
          title: "International WhatsApp Number",
          definition:
            "Must be entered with country code and no symbols (e.g., 51987654321, 15551234567). BotMix verifies the number to ensure message delivery.",
        },
        {
          id: "nombre_identificador",
          title: "Name & Business Alias",
          definition:
            "Visible identification across chats, inboxes, and tickets. You can update it manually or synchronize it with the customer's public WhatsApp profile.",
        },
        {
          id: "correo_electronico",
          title: "Email & Internal Notes",
          definition:
            "Complementary information for omnichannel messaging and historical relationship tracking.",
        },
      ],
    },
    {
      id: "campos_personalizados",
      title: "2. Dynamic Custom Fields",
      description:
        "Tailored attributes to enrich customer profile records.",
      items: [
        {
          id: "atributos_extra",
          title: "Extra Attributes (Key-Value)",
          definition:
            "Add custom fields like 'City', 'Tax ID', 'Birthday', 'Subscribed Plan', or 'Address'. These can be used as variables in quick messages and templates.",
        },
        {
          id: "variables_mensajes",
          title: "Usage in Messaging & Automations",
          definition:
            "Personalized attributes can be injected into automated messages for a 100% human and personalized customer experience.",
        },
      ],
    },
    {
      id: "billetera_responsable",
      title: "3. Sales Wallet & Account Owner Assignment",
      description:
        "Commercial governance and automated ticket routing.",
      items: [
        {
          id: "billetera_comercial",
          title: "Sales Wallet / Assigned Agent",
          definition:
            "Link the contact to a specific sales rep or department. When the customer contacts you, tickets can automatically route to their designated advisor.",
        },
        {
          id: "control_acceso",
          title: "Segmentation & Data Privacy",
          definition:
            "Restricts contact visibility based on advisor permissions and team roles.",
        },
      ],
    },
    {
      id: "import_export_sync",
      title: "4. Import, Export & WhatsApp Group Sync",
      description:
        "Bulk tools to migrate, back up, and expand your database.",
      items: [
        {
          id: "importacion_masiva",
          title: "Bulk Import (CSV / Excel)",
          definition:
            "Import existing customer lists with a single click. The wizard parses, validates, and cleans telephone numbers automatically.",
        },
        {
          id: "sincronizar_grupos",
          title: "WhatsApp Group Participant Sync",
          definition:
            "Automatically imports group members from connected WhatsApp groups directly into your BotMix contact directory for outreach campaigns.",
        },
        {
          id: "exportacion_datos",
          title: "Contact Exporting",
          definition:
            "Download your filtered audience in spreadsheet formats for reporting or external audit workflows.",
        },
        {
          id: "extraer_contactos_grupo",
          title: "Extract Group Contacts (Blue Button)",
          definition:
            "Downloads a CSV list of group participants. Security note: In groups with active privacy, WhatsApp anonymizes members as LID identifiers (15-digit codes) instead of phone numbers to protect their privacy.",
        },
        {
          id: "campana_directa_grupo",
          title: "Direct Campaign to Group Members (Red Button)",
          definition:
            "Allows sending one-on-one invitation messages to group participants without leaving the platform. Features real-time membership validation (only lines belonging to the group can send), forced Tier 1 warmup (max 30 contacts/day), and business hours window from 09:00 to 18:00 to prevent Meta bans.",
        },
        {
          id: "regla_seguridad_lids",
          title: "Incompatibility of LIDs in External Campaign Lists",
          definition:
            "⚠️ GOLDEN RULE: Lists exported from groups contain LIDs and MUST NOT be imported into regular campaign 'Contact Lists'. Meta detects unauthorized outreach to LIDs from external numbers as scraping and issues instant bans. The system automatically blocks uploading these files to protect your numbers.",
        },
      ],
    },
    {
      id: "etiquetado_filtros",
      title: "5. Tags, Brands & Smart Filters",
      description:
        "Agile classification for targeted campaigns and follow-ups.",
      items: [
        {
          id: "etiquetas_contacto",
          title: "Segmentation Tags",
          definition:
            "Assign color-coded labels to indicate lead status (e.g., 'VIP Client', 'Past Due', 'Hot Lead').",
        },
        {
          id: "filtro_marcas",
          title: "Brand Filtering & Instant Search",
          definition:
            "Filter your portfolio by business brand or find contacts immediately by phone, name, or company.",
        },
      ],
    },
  ],
};

const pt = {
  header: {
    badge: "Guia Oficial BotMix",
    title: "Gestão Integral de Contatos e Diretório CRM",
    summary:
      "Diretório centralizado de clientes, leads e grupos. Permite gerenciar nomes, números internacionais do WhatsApp, e-mails, campos personalizados dinâmicos, atribuição a carteiras comerciais e sincronização em massa por importação e exportação.",
  },
  sections: [
    {
      id: "datos_basicos",
      title: "1. Dados Básicos e Formato Telefônico",
      description:
        "Registro seguro da identidade e canais de contato.",
      items: [
        {
          id: "telefono_internacional",
          title: "Número Internacional WhatsApp",
          definition:
            "Deve ser inserido com o código do país sem caracteres especiais (ex: 5511999998888, 351912345678). O BotMix valida a numeração para garantir a entrega.",
        },
        {
          id: "nombre_identificador",
          title: "Nome e Identificador Comercial",
          definition:
            "Identificação visível nos chats e tickets. Pode ser editado manualmente ou sincronizado com a foto e perfil do WhatsApp do cliente.",
        },
        {
          id: "correo_electronico",
          title: "E-mail e Observações",
          definition:
            "Informações adicionais para comunicação multicanal e histórico completo de atendimento.",
        },
      ],
    },
    {
      id: "campos_personalizados",
      title: "2. Campos Personalizados Dinâmicos",
      description:
        "Atributos sob medida para enriquecer a ficha do cliente.",
      items: [
        {
          id: "atributos_extra",
          title: "Atributos Adicionais (Chave-Valor)",
          definition:
            "Cadastre campos como 'Cidade', 'CPF/CNPJ', 'Aniversário', 'Plano Contratado' ou 'Endereço'. Podem ser usados como variáveis em respostas rápidas.",
        },
        {
          id: "variables_mensajes",
          title: "Uso em Mensagens e Automações",
          definition:
            "Valores dinâmicos inseridos automaticamente para criar uma comunicação humanizada e personalizada.",
        },
      ],
    },
    {
      id: "billetera_responsable",
      title: "3. Atribuição de Carteira e Usuário Responsável",
      description:
        "Controle comercial e direcionamento ágil de tickets.",
      items: [
        {
          id: "billetera_comercial",
          title: "Carteira / Consultor Designado",
          definition:
            "Vincula o contato ao atendente responsável. Novos atendimentos podem ser encaminhados diretamente a esse profissional.",
        },
        {
          id: "control_acceso",
          title: "Segmentação e Privacidade",
          definition:
            "Controle de visualização de contatos de acordo com os níveis de permissão dos operadores.",
        },
      ],
    },
    {
      id: "import_export_sync",
      title: "4. Importação, Exportação e Grupos de WhatsApp",
      description:
        "Ferramentas em massa para migrar e sincronizar sua base de contatos.",
      items: [
        {
          id: "importacion_masiva",
          title: "Importação em Massa (CSV / Excel)",
          definition:
            "Suba planilhas completas de clientes com um clique. O assistente valida os números automaticamente.",
        },
        {
          id: "sincronizar_grupos",
          title: "Sincronização de Grupos do WhatsApp",
          definition:
            "Importa os participantes de grupos do WhatsApp conectados para a base de contatos do BotMix para futuras campanhas.",
        },
        {
          id: "exportacion_datos",
          title: "Exportação de Contatos",
          definition:
            "Baixe sua base filtrada para planilhas e relatórios externos.",
        },
        {
          id: "extraer_contactos_grupo",
          title: "Extração de Contatos de Grupos (Botão Azul)",
          definition:
            "Baixa uma lista em CSV dos participantes do grupo. Nota de segurança: Em grupos com privacidade ativa, o WhatsApp anonimiza os integrantes como identificadores LID (códigos de 15 dígitos) em vez de números de telefone.",
        },
        {
          id: "campana_directa_grupo",
          title: "Campanha Direta para Membros do Grupo (Botão Vermelho)",
          definition:
            "Permite enviar mensagens de convite 1 a 1 para os participantes do grupo sem sair da plataforma. Possui validação de membresia em tempo real (apenas a linha participante pode enviar), aquecimento forçado Tier 1 (máx. 30 contatos/dia) e janela comercial das 09:00 às 18:00 para evitar banimentos da Meta.",
        },
        {
          id: "regla_seguridad_lids",
          title: "Incompatibilidade de LIDs em Listas de Campanhas Externas",
          definition:
            "⚠️ REGRA DE OURO: As listas exportadas de grupos contêm LIDs e NÃO devem ser importadas em 'Listas de Contatos' de campanhas regulares. A Meta detecta mensagens não autorizadas para LIDs a partir de números externos como raspagem ilegal e aplica banimento imediato. O sistema bloqueia automaticamente o envio desses arquivos para proteger suas linhas.",
        },
      ],
    },
    {
      id: "etiquetado_filtros",
      title: "5. Etiquetas, Marcas e Filtros Inteligentes",
      description:
        "Organização prática para segmentação e disparos.",
      items: [
        {
          id: "etiquetas_contacto",
          title: "Etiquetas Coloridas de Segmentação",
          definition:
            "Identifique rapidamente o status de cada lead (ex: 'Cliente VIP', 'Inadimplente', 'Interessado').",
        },
        {
          id: "filtro_marcas",
          title: "Filtro por Marcas e Busca Instantânea",
          definition:
            "Filtre contatos por marca comercial ou localize-os imediatamente por nome, telefone ou empresa.",
        },
      ],
    },
  ],
};

const fr = {
  header: {
    badge: "Guide Officiel BotMix",
    title: "Gestion Complète des Contacts et Répertoire CRM",
    summary:
      "Répertoire centralisé de clients, prospects et groupes. Gérez les noms, numéros WhatsApp internationaux, e-mails, champs personnalisés dynamiques, portefeuilles commerciaux et synchronisation massive par import/export.",
  },
  sections: [
    {
      id: "datos_basicos",
      title: "1. Données de Base et Format Téléphonique",
      description:
        "Enregistrement sécurisé de l'identité et des coordonnées du contact.",
      items: [
        {
          id: "telefono_internacional",
          title: "Numéro International WhatsApp",
          definition:
            "Doit être saisi avec l'indicatif pays sans symboles spéciaux (ex: 33612345678, 51987654321). BotMix valide la numérotation pour garantir l'envoi.",
        },
        {
          id: "nombre_identificador",
          title: "Nom et Identifiant Commercial",
          definition:
            "Nom affiché dans les conversations et tickets. Peut être modifié manuellement ou synchronisé avec le profil public WhatsApp.",
        },
        {
          id: "correo_electronico",
          title: "E-mail et Notes Internes",
          definition:
            "Informations complémentaires pour le suivi historique et le support omnicanal.",
        },
      ],
    },
    {
      id: "campos_personalizados",
      title: "2. Champs Personnalisés Dynamiques",
      description:
        "Attributs sur mesure pour enrichir la fiche prospect/client.",
      items: [
        {
          id: "atributos_extra",
          title: "Attributs Supplémentaires (Clé-Valeur)",
          definition:
            "Ajoutez des champs comme 'Ville', 'Numéro de TVA', 'Date d'anniversaire', 'Formule souscrite' ou 'Adresse'. Utilisables comme variables dans les messages.",
        },
        {
          id: "variables_mensajes",
          title: "Utilisation dans les Messages et Automatisations",
          definition:
            "Insertion dynamique des valeurs pour une communication sur mesure et personnalisée.",
        },
      ],
    },
    {
      id: "billetera_responsable",
      title: "3. Attribution de Portefeuille et Conseiller Référent",
      description:
        "Gouvernance commerciale et routage intelligent des échanges.",
      items: [
        {
          id: "billetera_comercial",
          title: "Portefeuille / Conseiller Attitré",
          definition:
            "Associe le contact à un conseiller dédié. Les nouveaux échanges lui sont automatiquement attribués.",
        },
        {
          id: "control_acceso",
          title: "Segmentation et Confidentialité",
          definition:
            "Restreint l'accès aux contacts selon le profil et les permissions des agents.",
        },
      ],
    },
    {
      id: "import_export_sync",
      title: "4. Importation, Exportation et Groupes WhatsApp",
      description:
        "Outils en masse pour transférer, sauvegarder et enrichir votre base.",
      items: [
        {
          id: "importacion_masiva",
          title: "Importation Massive (CSV / Excel)",
          definition:
            "Importez vos bases de contacts en un clic avec nettoyage et validation automatique des numéros.",
        },
        {
          id: "sincronizar_grupos",
          title: "Synchronisation des Groupes WhatsApp",
          definition:
            "Extrait automatiquement les membres de vos groupes WhatsApp vers votre répertoire BotMix pour vos campagnes ciblées.",
        },
        {
          id: "exportacion_datos",
          title: "Exportation des Données",
          definition:
            "Téléchargez vos listes de contacts filtrées sous forme de tableur.",
        },
        {
          id: "extraer_contactos_grupo",
          title: "Extraction des Contacts de Groupes (Bouton Bleu)",
          definition:
            "Télécharge une liste CSV des participants du groupe. Remarque de sécurité : Dans les groupes avec confidentialité active, WhatsApp anonymise les membres sous forme d'identifiants LID (codes à 15 chiffres) plutôt que de numéros de téléphone.",
        },
        {
          id: "campana_directa_grupo",
          title: "Campagne Directe auprès des Membres du Groupe (Bouton Rouge)",
          definition:
            "Permet d'envoyer des messages d'invitation individuels aux membres du groupe directement. Comprend une validation d'adhésion en temps réel (seul le numéro membre du groupe peut envoyer), un envoi progressif Tier 1 (max 30 contacts/jour) et des horaires de bureau de 09h00 à 18h00 pour éviter les bannissements de Meta.",
        },
        {
          id: "regla_seguridad_lids",
          title: "Incompatibilité des LIDs dans les Listes de Campagnes Externes",
          definition:
            "⚠️ RÈGLE D'OR : Les listes exportées de groupes contiennent des LIDs et NE DOIVENT PAS être importées dans les 'Listes de Contacts' des campagnes classiques. Meta détecte les messages vers des LIDs depuis un numéro externe comme du scraping et applique un bannissement immédiat. Le système bloque automatiquement ces fichiers pour protéger vos numéros.",
        },
      ],
    },
    {
      id: "etiquetado_filtros",
      title: "5. Étiquettes, Marques et Filtres Intelligents",
      description:
        "Organisation efficace pour vos campagnes et relances.",
      items: [
        {
          id: "etiquetas_contacto",
          title: "Étiquettes de Segmentation",
          definition:
            "Attribuez des étiquettes de couleur pour classifier vos contacts (ex: 'VIP', 'En retard', 'Chaud').",
        },
        {
          id: "filtro_marcas",
          title: "Filtre par Marque et Recherche Rapide",
          definition:
            "Segmentez par marque commerciale ou retrouvez immédiatement un contact par nom, numéro ou entreprise.",
        },
      ],
    },
  ],
};

const ar = {
  header: {
    badge: "دليل BotMix الرسمي",
    title: "الإدارة الشاملة لجهات الاتصال ودليل CRM",
    summary:
      "دليل مركزي لإدارة العملاء والعملاء المحتملين والمجموعات. يتيح إدارة الأسماء، أرقام واتساب الدولية، البريد الإلكتروني، الحقول المخصصة الديناميكية، تعيين المحافظ التجارية والمزامنة الشاملة عبر الاستيراد والتصدير.",
  },
  sections: [
    {
      id: "datos_basicos",
      title: "1. البيانات الأساسية وتنسيق أرقام الهواتف",
      description:
        "تسجيل آمن لهوية جهة الاتصال وقنوات التواصل الخاصة بها.",
      items: [
        {
          id: "telefono_internacional",
          title: "رقم واتساب الدولي",
          definition:
            "يجب إدخاله برمز الدولة وبدون رموز خاصة (مثال: 966501234567، 51987654321). يقوم BotMix بالتحقق من صحة الرقم لضمان تسليم الرسائل.",
        },
        {
          id: "nombre_identificador",
          title: "الاسم والمعرّف التجاري",
          definition:
            "الاسم الظاهر في المحادثات والتذاكر. يمكنك تعديله يدوياً أو مزامنته تلقائياً مع ملف تعريف واتساب الخاص بالعميل.",
        },
        {
          id: "correo_electronico",
          title: "البريد الإلكتروني والملاحظات",
          definition:
            "معلومات تكميلية للتواصل متعدد القنوات وسجل المتابعة التاريخي.",
        },
      ],
    },
    {
      id: "campos_personalizados",
      title: "2. الحقول المخصصة الديناميكية",
      description:
        "سمات مرنة ومخصصة لإثراء ملف العميل.",
      items: [
        {
          id: "atributos_extra",
          title: "سمات إضافية (مفتاح - قيمة)",
          definition:
            "إضافة حقول مثل 'المدينة'، 'الرقم الضريبي'، 'تاريخ الميلاد'، 'نوع الخطة' أو 'العنوان'. يمكن استخدامها كمتغيرات في الردود السريعة والقوالب.",
        },
        {
          id: "variables_mensajes",
          title: "الاستخدام في الرسائل والأتمتة",
          definition:
            "إدراج البيانات المخصصة ديناميكياً في نصوص الرسائل لتقديم تجربة عملاء شخصية واحترافية.",
        },
      ],
    },
    {
      id: "billetera_responsable",
      title: "3. تعيين المحفظة والمستشار المسؤول",
      description:
        "الحوكمة التجارية وتوجيه المحادثات تلقائياً.",
      items: [
        {
          id: "billetera_comercial",
          title: "المحفظة التجارية / المستشار المعيّن",
          definition:
            "ربط جهة الاتصال بوكيل أو قسم تجاري محدد. يتم توجيه تذاكر العميل الواردة تلقائياً إلى المستشار المسؤول عنه.",
        },
        {
          id: "control_acceso",
          title: "التقسيم وخصوصية البيانات",
          definition:
            "تقييد صلاحية رؤية جهات الاتصال وفقاً لأدوار وصلاحيات فريق العمل.",
        },
      ],
    },
    {
      id: "import_export_sync",
      title: "4. الاستيراد والتصدير ومجموعات واتساب",
      description:
        "أدوات جماعية لنقل ونسخ وتوسيع قاعدة بياناتك.",
      items: [
        {
          id: "importacion_masiva",
          title: "استيراد جماعي (CSV / Excel)",
          definition:
            "رفع قوائم العملاء بنقرة واحدة، مع معالجة وتحقق تلقائي من صحة أرقام الهواتف.",
        },
        {
          id: "sincronizar_grupos",
          title: "مزامنة أعضاء مجموعات واتساب",
          definition:
            "استيراد جميع المشاركين في مجموعات واتساب المتصلة مباشرة إلى دليل BotMix لحملات التوعية والترويج.",
        },
        {
          id: "exportacion_datos",
          title: "تصدير جهات الاتصال",
          definition:
            "تنزيل قاعدة البيانات المفلترة بتنسيق جداول البيانات للتقارير والتدقيق الخارجي.",
        },
        {
          id: "extraer_contactos_grupo",
          title: "استخراج جهات اتصال المجموعات (الزر الأزرق)",
          definition:
            "تنزيل قائمة بتنسيق CSV للمشاركين في المجموعة. ملاحظة أمنية: في المجموعات ذات الخصوصية النشطة، يقوم واتساب بإخفاء أرقام الأعضاء وتحويلها إلى معرفات LID (أكواد مكونة من 15 رقمًا) بدلاً من أرقام الهواتف لحماية خصوصيتهم.",
        },
        {
          id: "campana_directa_grupo",
          title: "حملة مباشرة لأعضاء المجموعة (الزر الأحمر)",
          definition:
            "يتيح إرسال رسائل دعوة فردية لمشاركي المجموعة مباشرة من المنصة. يتميز بالتحقق المباشر من عضوية الخط في المجموعة، والحد الأمني التلقائي من المستوى 1 (30 جهة اتصال/يوم)، ونافذة عمل من 09:00 إلى 18:00 لتجنب حظر Meta.",
        },
        {
          id: "regla_seguridad_lids",
          title: "عدم توافق معرفات LID في قوائم الحملات الخارجية",
          definition:
            "⚠️ قاعدة ذهبية: تحتوي القوائم المستخرجة من المجموعات على معرفات LID ويُحظر استيرادها في 'قوائم جهات الاتصال' للحملات العادية. تكشف خوارزميات Meta المراسلات غير المصرح بها لمعرفات LID من أرقام خارجية وتفرض حظرًا فوريًا. يقوم النظام بحظر تحميل هذه الملفات تلقائيًا لحماية خطوطك.",
        },
      ],
    },
    {
      id: "etiquetado_filtros",
      title: "5. الوسوم، العلامات التجارية والتصفية الذكية",
      description:
        "تنظيم مرن لحملات المتابعة والتسويق.",
      items: [
        {
          id: "etiquetas_contacto",
          title: "وسوم التصنيف الملونة",
          definition:
            "تصنيف العملاء بألوان مميزة لتحديد حالاتهم (مثل: 'عميل VIP'، 'متأخر في السداد'، 'مهتم جداً').",
        },
        {
          id: "filtro_marcas",
          title: "تصفية العلامات التجارية والبحث الفوري",
          definition:
            "تصفية العملاء حسب العلامة التجارية أو البحث الفوري بالاسم أو رقم الهاتف أو الشركة.",
        },
      ],
    },
  ],
};

export const CONTACTS_HELP_DATA = {
  es,
  en,
  pt,
  fr,
  ar,
};
